import React, { useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { getLanguageCode, translate } from '../../utils/translation'
import { CloseButton, OutlineButton, PrimaryButton } from '../buttons/Buttons'
import Modal, { ModalContent, ModalHeader } from '../modal/Modal'
import { fonts, mediaQueries, palette } from '../../styles/variables'
import { checkoutModalHandler } from '../../utils/sendMessage'
import { MIXPANEL_PROPS } from '../../utils/mixpanel'
import { clearToken } from '../../utils/storage'
import { sendMessage } from '../../utils/functions'
import { selectCurrentUser } from '../../state/selectors'

const desktopStyle = {
  minWidth: '480px',
  width: '480px',
  overflow: 'unset',
  overflowY: 'hidden',
}

const mobileStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}

const Title = styled.h3`
  font-size: 24px;
  color: ${palette.darkGray};
  font-weight: 400;
  margin: 0;
  line-height: 33.6px;

  b {
    color: ${palette.darkGray};
    font-size: 24px;
    font-family: ${fonts.primaryBold};
    font-weight: 700;
  }
`

const Text = styled.p`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  margin: 0;
  margin-bottom: 24px;

  ${[mediaQueries.smallMobile]} {
    margin-inline: auto;
  }
`

const Footer = styled.div`
  margin-bottom: 24px;
  border-top: 1px solid ${palette.lightGray50};
  padding: 24px 24px 0px;

  ${[mediaQueries.mobile]} {
    padding: 24px 24px 0px;
  }

  > div {
    display: flex;
    justify-content: center;
    flex-direction: column;

    & button {
      flex: 1;
      font-size: 14px;
      width: 100%;
      height: 48px;
      min-height: 48px;
      padding-block: 0;
      display: block;
      line-height: normal;
      margin-left: 0;
    }

    & button:nth-child(1) {
      margin-bottom: 12px;
    }
  }

  p {
    text-align: center;
    font-size: 14px;
    margin-top: 24px;
    font-weight: 400;

    button {
      font-size: 14px;
      padding-left: 4px;
      background: none;
      border: none;
      color: ${palette.orange};
      text-decoration-line: underline;
    }
  }
`

const Button = styled.button`
  background: none;
  border: none;
  margin-left: ${({ isImageExport }) => (isImageExport ? 0 : 16)}px;
  padding: 0;
`

export const UnloggedModal = ({ isImageExport, children }) => {
  const user = useSelector(selectCurrentUser())

  const isSignedIn = !!user.id
  const [isOpen, setIsOpen] = useState(false)
  const openCheckoutModal = () => {
    onClose()
    checkoutModalHandler({
      mixpanelProps: [
        MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.EXPORT_PDF_IMG_MODAL,
        MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL,
      ],
    })
  }

  const onClose = () => setIsOpen(false)

  const redirectLogin = () => {
    clearToken()

    sendMessage('SHOW_AUTH', {
      type: 'login',
    })
  }

  return (
    <>
      <Button
        isImageExport={isImageExport}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {children}
      </Button>
      <Modal
        isOpen={isOpen}
        desktopStyle={desktopStyle}
        mobileMaxWidth={480}
        overlayStyle={{ zIndex: 10000 }}
        mobileStyle={mobileStyle}
      >
        <CloseButton
          onClick={onClose}
          styles={{
            position: 'absolute',
            top: getLanguageCode() === 'fr' ? 8 : 16,
            right: 16,
            zIndex: 999,
          }}
        />
        <ModalHeader
          padding="24px 24px 0 24px"
          after={{ margin: '10px auto auto', width: 56 }}
        >
          <Title
            dangerouslySetInnerHTML={{
              __html: translate('export_report_upgrade_heading'),
            }}
          />
        </ModalHeader>
        <ModalContent
          styles={{
            padding: '24px 24px 0px',
            [mediaQueries.mobile]: {
              padding: '24px 24px 0px',
            },
          }}
        >
          <Text>{translate('export_report_upgrade_cta')}</Text>
        </ModalContent>
        <Footer>
          <div>
            <PrimaryButton
              theme="orange"
              tag={translate('start_trial_button')}
              onClick={openCheckoutModal}
            />
            <OutlineButton color={palette.gray} onClick={onClose} static>
              {translate('no_thanks')}
            </OutlineButton>
          </div>
          {!isSignedIn && (
            <p>
              {translate('have_subscription')}
              <button type="button" onClick={redirectLogin}>
                {translate('sign_in_here')}
              </button>
            </p>
          )}
        </Footer>
      </Modal>
    </>
  )
}
